import { makeStyles } from '@material-ui/core/styles';
import arrayShuffle from 'array-shuffle';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { sectionHeading } from '@/styles/sectionHeading.ts';

import { useCategoryBrowser } from '@/hooks/use-category-browser.ts';

import { useConfigStore, useSearchStore } from '@/store.js';

import SearchResultCard, { SearchResultType } from './SearchResultCard.jsx';

const COLLAPSED_LIMIT = 8;

const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        width: '100%',
        maxWidth: theme.layout.contentWidth,
        margin: '2.5rem auto 0 auto',
    },
    heading: sectionHeading(theme),
    cards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
        width: '100%',
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    showAll: {
        padding: 0,
        border: 'none',
        background: 'none',
        fontFamily: 'inherit',
        fontSize: '0.875rem',
        fontWeight: theme.weight.body,
        color: theme.palette.common.darkGrey,
        textDecoration: 'underline',
        textDecorationColor: theme.palette.common.yellow,
        textDecorationThickness: 2,
        textUnderlineOffset: '0.2em',
        cursor: 'pointer',
        '&:hover, &:focus-visible': {
            textDecorationThickness: 3,
        },
    },
}));

const LocationProfessionals = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const professionals = useSearchStore((state) => state.professionals);
    const allDocuments = useSearchStore((state) => state.allDocuments);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);
    const { showCategoryBrowser: hasOwnBrowser } = useCategoryBrowser();

    const [browseContext, setBrowseContext] = useState(selectedLocation);
    if (browseContext !== selectedLocation) {
        setBrowseContext(selectedLocation);
        setExpanded(false);
    }

    const atLocation = useMemo(
        () =>
            arrayShuffle(
                Object.values(professionals ?? {}).filter(
                    (professional) =>
                        allDocuments?.[professional.location_hub?.id]?.key?.[0]?.text === selectedLocation &&
                        professional.display_name?.[0]?.text
                )
            ),
        [professionals, allDocuments, selectedLocation]
    );

    if (hasOwnBrowser || atLocation.length < 2) return null;

    const visible = expanded ? atLocation : atLocation.slice(0, COLLAPSED_LIMIT);

    return (
        <div className={classes.wrapper}>
            <p className={classes.heading}>{t('DoctorsAtThisLocation')}</p>
            <div className={classes.cards}>
                {visible.map((professional) => (
                    <SearchResultCard
                        key={professional.key[0].text}
                        cardKey={professional.key[0].text}
                        prismicData={professional}
                        searchResultType={SearchResultType.PROFESSIONAL}
                    />
                ))}
            </div>
            {atLocation.length > visible.length && (
                <button type="button" className={classes.showAll} onClick={() => setExpanded(true)}>
                    {t('ShowAllDoctors')}
                </button>
            )}
        </div>
    );
};

export default LocationProfessionals;
